// @flow
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import update from 'immutability-helper';
import { defineMessages, FormattedMessage, intlShape } from 'react-intl';
import { Link } from 'react-router-dom';
import Loading from '../../../../elements/Loading/Loading';
import InfiniteScroll from '../../../../elements/InfiniteScroll/InfiniteScroll';
import Filter from '../../../../elements/Filter/Filter';
import FilterDrawer from '../../../../elements/Filter/Drawer/FilterDrawer';
import MultiImage from '../../../../elements/MultiImage/MultiImage';
import { generateTags } from '../../../../elements/Filter/utils';
import classNames from 'classnames/bind';
import { orderVerifier } from '../../../../utils/string';
import { stateConverter } from '../../../order/utils/stateConverter';
import { getLocalizedCountry } from '../../../local-values/utils/countries';
import AlertContainer from '../../../alert/container/AlertContainer';
import OrderBox from '../OrderBox/OrderBox';

import commonStyle from '../OrderViewCommon.scss';
import filterOptionsStyle from '../../../../assets/styles/filter-options.scss';
import filterStyles from '../../../../assets/styles/filter.scss';
import style from './PurchasesPasts.scss';

import icons from '../../../../assets/styles/icons.scss';
import type { LocalValuesState } from '../../../local-values/duck';
import type { Category, Order } from '../../../../entities';
import type { FilterCheckbox } from '../../../../elements/Filter/Filter';
import type { StateConvertResponse } from '../../../order/utils/stateConverter';
import type { HTTPError } from '../../../../error';
import type { LocaleState } from '../../../locale/duck';

type Props = {
  +resetOrdersState: () => void;
  +localeState: LocaleState;
  +localValues: LocalValuesState;
  +page: number;
  +canAsk?: boolean;
  +pastPurchases?: Order[];
  +isFetchingPasts?: boolean;
  +fetchPastPurchases: (queryFilter?: { [key: string]: string }, resetPagination?: boolean) => void;
  +fetchPastError?: HTTPError;
  +resetAlertState: () => void;
};

type State = {
  drawerOpen: boolean;
  filter: {
    name: {
      value: string;
    };
    openName: boolean;
    categories: {
      [key: string]: boolean;
    };
    openCategories: boolean;
    years: {
      [key: string]: boolean;
    };
    openYears: boolean;
  };
  sort: {
    value: string;
    name: string;
  };
  order: {
    value: 'ASC' | 'DESC';
  };
};

const messages = defineMessages({
  title: {
    id: 'orders.pasts.document.title',
  },
  description: {
    id: 'orders.pasts.document.description',
  },
  sortPlaceholder: {
    id: 'sort.title',
  },
  sortMostRecent: {
    id: 'sort.option.mostRecent',
  },
  sortLatest: {
    id: 'sort.option.latest',
  },
  sortHighestPrice: {
    id: 'sort.option.highestPrice',
  },
  sortLowestPrice: {
    id: 'sort.option.lowestPrice',
  },
});

const initialState = {
  drawerOpen: false,
  filter: {
    name: {
      value: '',
    },
    openName: true,
    categories: {},
    openCategories: false,
    years: {},
    openYears: false,
  },
  sort: {
    value: '',
    name: '',
  },
  order: {
    value: 'DESC',
  },
};
export default class PurchasesPasts extends Component<Props, State> {
  state = initialState;

  // Only in browser
  componentDidMount() {
    const { fetchPastPurchases, pastPurchases } = this.props;
    if (!pastPurchases) {
      fetchPastPurchases();
    }
  }

  componentWillUnmount() {
    const { resetOrdersState, resetAlertState } = this.props;
    resetOrdersState();
    resetAlertState();
  }

  handleOpenDrawer = () => {
    this.setState({ drawerOpen: true });
  };

  handleCloseDrawer = () => {
    const { fetchPastPurchases } = this.props;
    this.setState({
      drawerOpen: false,
    });
    fetchPastPurchases(this.buildQueryParams(), true);
  };

  handleOpen = (name: string) => {
    this.setState((state: State) => {
      return {
        filter: update(state.filter, {
          [name]: { $set: !state.filter[name] },
        }),
      };
    });
  };

  handleChangeName(e: Event, isDesktop: boolean = true) {
    if (e.target instanceof HTMLInputElement) {
      const { value } = e.target;
      this.handleChangeFilterValueCreator(isDesktop)('name', { value });
    }
  }

  handleChangeCategory(e: Event, isDesktop: boolean = true) {
    const { filter } = this.state;
    if (e.target instanceof HTMLInputElement) {
      const { value } = e.target;
      this.handleChangeFilterValueCreator(isDesktop)('categories', { [value]: !filter.categories[value] });
    }
  }

  handleChangeYear(e: Event, isDesktop: boolean = true) {
    const { filter } = this.state;
    if (e.target instanceof HTMLInputElement) {
      const { value } = e.target;
      this.handleChangeFilterValueCreator(isDesktop)('years', { [value]: !filter.years[value] });
    }
  }

  buildQueryParams(): { [key: string]: string } {
    const params = {};
    const { filter, sort, order } = this.state;
    const categories = Object.keys(filter.categories)
      .reduce((acum, act: string) => filter.categories[act] ? acum.concat(act) : acum, []);
    const years = Object.keys(filter.years)
      .reduce((acum, act: string) => filter.years[act] ? acum.concat(act) : acum, []);
    if (filter.name.value) {
      if (orderVerifier(filter.name.value)) params.order_id = filter.name.value;
      else params.query = filter.name.value;
    }
    if (categories.length) params.categories = categories.join(',');
    if (years.length) params.years = years.join(',');
    // Sort & Ord
    if (sort.name) params.sort = sort.name;
    if (order.value) params.order = order.value;
    return params;
  }

  handleChangeSort = (e: Event) => {
    const { fetchPastPurchases } = this.props;
    if (e.target instanceof HTMLSelectElement) {
      const option = e.target.options[e.target.selectedIndex];
      const dataset: { sort: string, order: 'ASC' | 'DESC' } = (option.dataset: any);
      this.setState({
        sort: {
          value: option.value,
          name: dataset.sort,
        },
        order: {
          value: dataset.order,
        },
      }, () => fetchPastPurchases(this.buildQueryParams(), true));
    }
  };

  handleClearFilters = () => {
    const { fetchPastPurchases } = this.props;
    this.setState(initialState,
      () => fetchPastPurchases(this.buildQueryParams(), true));
  };

  handleDeleteTag = (key?: string, name?: string) => {
    const { fetchPastPurchases } = this.props;
    const updateQuery = {};
    switch (key) {
      case 'categories':
      case 'years':
        if (name) {
          updateQuery[key] = {
            [name]: { $set: false },
          };
        }
        break;
      case 'name':
        updateQuery[key] = {
          value: { $set: '' },
        };
        break;
    }
    this.setState((prevState: State) => update(prevState, {
      filter: updateQuery,
    }), () => fetchPastPurchases(this.buildQueryParams(), true));
  };

  // Depends if desktop or mobile
  handleChangeFilterValueCreator = (isDesktop: boolean) =>
    (name: string, value: any) => {
      const { fetchPastPurchases } = this.props;
      this.setState((state: State) => {
        const c = update(state.filter[name], { $merge: value });
        return {
          filter: update(state.filter, {
            [name]: { $set: c },
          }),
        };
      }, () => {
        if (isDesktop) {
          fetchPastPurchases(this.buildQueryParams(), true);
        }
      });
    };

  generateFilterFields = (categories: ?FilterCheckbox[], years: ?FilterCheckbox[], { filter }: State, isDesktop: boolean) => [
    {
      type: 'text',
      titleIntl: 'filter.name.or.order.title',
      placeholderIntl: 'filter.name.placeholder',
      handler: (e: Event) => this.handleChangeName(e, isDesktop),
      stateValue: filter.name,
      name: 'name',
      icon: icons.symbolSearch,
      isOpen: filter.openName,
      handleOpen: () => this.handleOpen('openName'),
    },
    {
      type: 'checkbox',
      titleIntl: 'filter.categories.title',
      handler: (e: Event) => this.handleChangeCategory(e, isDesktop),
      stateValue: filter.categories,
      name: 'categories',
      checkboxes: categories,
      isOpen: filter.openCategories,
      handleOpen: () => this.handleOpen('openCategories'),
    },
    {
      type: 'checkbox',
      titleIntl: 'filter.years.title',
      handler: (e: Event) => this.handleChangeYear(e, isDesktop),
      stateValue: filter.years,
      name: 'years',
      checkboxes: years,
      isOpen: filter.openYears,
      handleOpen: () => this.handleOpen('openYears'),
    },
  ];

  render() {
    const { drawerOpen, sort } = this.state;
    const { localValues, pastPurchases, isFetchingPasts, fetchPastPurchases, canAsk, localeState } = this.props;
    const categories = localValues.categories && localValues.categories.map((c: Category) => (
      {
        label: c.translations[localeState.locale.toUpperCase()].name,
        value: c.id.toString(),
      }
    ));
    const years = [
      {
        label: '2017',
        value: '2017',
      },
      {
        label: '2018',
        value: '2018',
      },
      {
        label: '2019',
        value: '2019',
      },
    ];

    const filterFields = this.generateFilterFields(categories, years, this.state, true);
    const filterContent = <Filter filterFields={(this.generateFilterFields(categories, years, this.state, true): any)}
                                  handleDeleteTag={this.handleDeleteTag}/>;
    const tags: any = generateTags((filterFields: any), this.handleDeleteTag, this.context.intl.formatMessage);
    const sortNativeSelect = (
      <select value={sort.value}
              onChange={this.handleChangeSort}>
        <option value="mostRecent" data-sort="DATE"
                data-order="DESC">{this.context.intl.formatMessage(messages.sortMostRecent)}</option>
        <option value="latest" data-sort="DATE"
                data-order="ASC">{this.context.intl.formatMessage(messages.sortLatest)}</option>
        <option value="highestPrice" data-sort="TOTAL"
                data-order="DESC">{this.context.intl.formatMessage(messages.sortHighestPrice)}</option>
        <option value="lowestPrice" data-sort="TOTAL"
                data-order="ASC">{this.context.intl.formatMessage(messages.sortLowestPrice)}</option>
      </select>
    );

    const noResult = (!pastPurchases || !pastPurchases.length) && !isFetchingPasts;

    /*const orders = pastPurchases && pastPurchases.map((purchase: Order, n: number) => {
            const stateResponse: StateConvertResponse = stateConverter(false, purchase.state);
            return (<Link to={`/purchase/order/${purchase.id}`} key={n}>
              <section className={commonStyle.imageWrap}>
                <MultiImage items={purchase.items}/>
              </section>
              <section className={commonStyle.textWrap}>
                <FormattedMessage id={stateResponse.labelIntlShort}>{
                  text => <p>{text} {stateResponse.innerState === '3p'
                  && ` ${getLocalizedCountry(purchase.deliveryCountry.code, this.context.intl.formatMessage, localValues.countriesMap)}`}</p>
                }</FormattedMessage>
              </section>
            </Link>);
          }
        );*/

    const orders = pastPurchases && pastPurchases
      .map((order: Order, n: number) => <OrderBox key={n}
                                                  localValues={localValues}
                                                  order={order}/>);
    return (
      <Fragment>
        <Helmet key="helmet">
          <title>{this.context.intl.formatMessage(messages.title)}</title>
          <meta name="description" content={this.context.intl.formatMessage(messages.description)}/>
        </Helmet>
        <section className={filterOptionsStyle.filterOptions}>
          <section className={filterOptionsStyle.header}>
            <FormattedMessage id="stores.filter.title">{
              text => <p className={filterOptionsStyle.title}><i className={icons.symbolTweak}/>{text}</p>
            }</FormattedMessage>
            <section className={filterOptionsStyle.sortOptions}>
              <FormattedMessage id="sort.title">{
                txt => <label>{txt}</label>
              }</FormattedMessage>
              {sortNativeSelect}
              {/*<i className={classNames(filterOptionsStyle.down, icons.iconChevronDownMedium)}/>
              <i className={classNames(filterOptionsStyle.up, icons.iconChevronUpMedium)}/>*/}
            </section>
            <section className={filterOptionsStyle.filterDrawer}>
              <FilterDrawer drawerOpen={drawerOpen}
                            filterFields={(this.generateFilterFields(categories, years,
                              this.state, false): any)}
                            color="lightBlue"
                            handleCloseDrawer={this.handleCloseDrawer}
                            handleOpenDrawer={this.handleOpenDrawer}
                            handleClearFilters={this.handleClearFilters}/>
            </section>
          </section>
        </section>
        <section key="mainWrap" className={classNames(commonStyle.mainWrap, commonStyle.nonPaddingTop)}>
          <aside className={commonStyle.filterWrap}>
            {filterContent}
          </aside>
          <section className={commonStyle.rightWrap}>
            <AlertContainer showId="orders"/>
            {!!tags.length && <ul className={filterStyles.tags}>
              {tags}
            </ul>}
            {noResult && <header>
              <FormattedMessage id="orders.pasts.noResults">{
                txt => <p>{txt}</p>
              }</FormattedMessage>
            </header>}
            <InfiniteScroll offset={650}
                             disabled={isFetchingPasts || !canAsk}
                             handleCheckpoint={() => fetchPastPurchases(this.buildQueryParams())}>
              <section>
                {orders}
              </section>
            </InfiniteScroll>
            {isFetchingPasts && <Loading className={commonStyle.loading}/>}
          </section>
        </section>
      </Fragment>);

  }
}

PurchasesPasts.propTypes = {
  resetOrdersState: PropTypes.func.isRequired,
  localeState: PropTypes.object.isRequired,
  localValues: PropTypes.object.isRequired,
  page: PropTypes.number,
  canAsk: PropTypes.bool,
  pastPurchases: PropTypes.array,
  isFetchingPasts: PropTypes.bool,
  fetchPastPurchases: PropTypes.func.isRequired,
  fetchPastError: PropTypes.func,
  resetAlertState: PropTypes.func.isRequired,
};

PurchasesPasts.contextTypes = {
  intl: intlShape,
};



// WEBPACK FOOTER //
// ./src/modules/orders/components/PurchasesPasts/PurchasesPasts.jsx